import type { DesgloseImportacion } from '@/shared/api/controllers/importaciones';
import { formatMoney } from '@/shared/lib/money';

type Codigo = 'ARS' | 'USD' | 'MXN';

/** Orden fijo de columnas: dólares primero (la mercadería suele venir en USD). */
const ORDEN: Codigo[] = ['USD', 'ARS', 'MXN'];

/**
 * Lo que cuesta el contenedor, separado en la mercadería (líneas de producto
 * de las compras) y cada gasto de importación (fletes, aduana… por cuenta
 * contable). Netos sin IVA y por moneda: un flete en pesos no se suma a una
 * factura en dólares.
 */
export function DesgloseCosto({ desglose }: { desglose: DesgloseImportacion }) {
  const filas: Array<{ concepto: string; montos: Record<string, number>; mercaderia?: boolean }> = [];
  if (Object.keys(desglose.mercaderia).length > 0) {
    filas.push({ concepto: 'Mercadería', montos: desglose.mercaderia, mercaderia: true });
  }
  for (const g of desglose.gastos) filas.push({ concepto: g.cuenta, montos: g.montos });
  if (Object.keys(desglose.sinDetalle).length > 0) {
    filas.push({ concepto: 'Compras sin detalle', montos: desglose.sinDetalle });
  }
  if (filas.length === 0) return null;

  const monedas = ORDEN.filter((m) => filas.some((f) => f.montos[m] != null));
  const total = (m: string) => filas.reduce((t, f) => t + (f.montos[m] ?? 0), 0);
  const totalGastos = (m: string) =>
    filas.filter((f) => !f.mercaderia).reduce((t, f) => t + (f.montos[m] ?? 0), 0);

  return (
    <section className="min-w-0 space-y-2">
      <div className="flex min-h-9 items-center gap-2">
        <h2 className="text-sm font-semibold">Costo del contenedor</h2>
        <span className="text-xs text-muted-foreground">Mercadería y gastos de importación, sin IVA</span>
      </div>
      <div className="overflow-x-auto rounded-2xl border bg-card">
        <table className="w-full text-sm">
          <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-2 font-medium">Concepto</th>
              {monedas.map((m) => (
                <th key={m} className="px-4 py-2 text-right font-medium">
                  {m}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filas.map((f) => (
              <tr key={f.concepto}>
                <td className={f.mercaderia ? 'px-4 py-2 font-medium text-foreground' : 'px-4 py-2 text-muted-foreground'}>
                  {f.concepto}
                </td>
                {monedas.map((m) => (
                  <td key={m} className="whitespace-nowrap px-4 py-2 text-right tabular-nums">
                    {f.montos[m] != null ? formatMoney(f.montos[m], m) : '—'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t bg-muted/40">
            {filas.some((f) => !f.mercaderia) && (
              <tr className="text-muted-foreground">
                <td className="px-4 py-2">Gastos de importación</td>
                {monedas.map((m) => (
                  <td key={m} className="whitespace-nowrap px-4 py-2 text-right tabular-nums">
                    {formatMoney(totalGastos(m), m)}
                  </td>
                ))}
              </tr>
            )}
            <tr className="font-semibold">
              <td className="px-4 py-2">Total</td>
              {monedas.map((m) => (
                <td key={m} className="whitespace-nowrap px-4 py-2 text-right tabular-nums">
                  {formatMoney(total(m), m)}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}
